/**
 * HistoryItem — single past-ride entry shown in the passenger's
 * "Recent trips" list. Handles pay-with-card / rate-driver follow-ups
 * for completed rides and an expandable VAT-breakdown receipt.
 *
 * iter-79 decomposition slice 5 of Passenger.jsx.
 *
 * Strict isolation contract (matches Slices 1-4 playbook):
 *   - Zero behavioural change vs. the original inline definition.
 *   - All props, helpers, and rendering logic preserved exactly.
 *   - No dispatch / state-machine / janitor / offer-routing logic touched.
 *
 * Props:
 *   ride      — historical ride object
 *   onPayCard — () => void  (only invoked when status==='completed' & card unpaid)
 *   onRate    — () => void  (only invoked when status==='completed' & no rating yet)
 */
import React, { useState } from "react";
import { Star, Receipt, Briefcase, RotateCcw } from "lucide-react";
import { FareBreakdown } from "./FareBreakdown";
import { statusMap } from "./RideStatusPanel";

export const HistoryItem = ({ ride, onPayCard, onRate }) => {
  const s = statusMap[ride.status] || { label: ride.status, color: "#9CA3AF" };
  const needsPayment = ride.status === "completed" && ride.payment_method === "card" && ride.payment_status !== "paid";
  const canRate = ride.status === "completed" && !ride.passenger_rating;
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border p-3 transition-card" data-testid={`history-item-${ride.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs truncate">
            {ride.pickup_address} → {ride.dropoff_address}
            {ride.stops?.length > 0 && (
              <span className="text-muted-foreground"> · {ride.stops.length} stop{ride.stops.length > 1 ? "s" : ""}</span>
            )}
          </div>
          <div className="label-mini text-muted-foreground mt-1 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }} />
            {s.label}
            {ride.passenger_rating && (
              <span className="flex items-center gap-0.5 ml-2">
                <Star className="w-3 h-3 fill-[#FFC107] text-[#FFC107]" />
                <span>{ride.passenger_rating}</span>
              </span>
            )}
          </div>
        </div>
        <div className="font-mono-tab font-bold text-sm">£{ride.fare || ride.estimated_fare}</div>
      </div>
      {ride.fare_breakdown && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-2 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
          data-testid={`history-receipt-toggle-${ride.id}`}
        >
          <Receipt className="w-3 h-3" /> {open ? "Hide" : "Show"} receipt (VAT breakdown)
        </button>
      )}
      {open && ride.fare_breakdown && (
        <div className="mt-2 p-2 bg-secondary/30 border border-border" data-testid={`history-receipt-${ride.id}`}>
          <FareBreakdown b={ride.fare_breakdown} compact />
        </div>
      )}
      {ride.refund_total > 0 && (
        <div className="mt-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-[#FEF2F2] text-[#EF4444] border border-[#EF4444]" data-testid={`history-refund-${ride.id}`} title={(ride.refunds || []).map((r) => `£${Number(r.amount).toFixed(2)} — ${r.reason || "refund"}`).join("\n")}>
          <RotateCcw className="w-3 h-3" /> Refunded £{Number(ride.refund_total).toFixed(2)}
        </div>
      )}
      {ride.is_account_ride && (
        <div className="mt-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-[#EFF3FF] text-[#002FA7] border border-[#002FA7]">
          <Briefcase className="w-3 h-3" /> ACCOUNT · {ride.account_company || "Invoiced"}
        </div>
      )}
      {(needsPayment || canRate) && (
        <div className="mt-2 flex gap-1.5">
          {needsPayment && (
            <button onClick={onPayCard} className="text-xs px-2 py-1 bg-[#002FA7] text-white hover:bg-[#001f6e] transition-colors" data-testid={`pay-history-${ride.id}`}>
              Pay £{ride.fare}
            </button>
          )}
          {canRate && (
            <button onClick={onRate} className="text-xs px-2 py-1 border border-border hover:bg-secondary transition-colors flex items-center gap-1" data-testid={`rate-history-${ride.id}`}>
              <Star className="w-3 h-3" /> Rate
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default HistoryItem;
